<template>
  <div class="category-settings">
    <!-- カテゴリ追加 -->
    <div class="add-category mb-4">
      <v-select
        v-model="selectedTag"
        :items="availableTags"
        item-text="name"
        item-value="id"
        label="カテゴリを追加"
        :prepend-icon="icons.mdiPlus"
        outlined
        dense
        hide-details
        :disabled="availableTags.length === 0"
        @change="handleAddCategory"
      />
    </div>

    <!-- カテゴリ一覧 -->
    <div
      v-if="localCategories.length > 0"
      ref="sortableContainer"
    >
      <v-list
        dense
        class="category-list"
      >
        <v-list-item
          v-for="category in localCategories"
          :key="category.vendibleTagId"
          :data-id="category.vendibleTagId"
          class="category-item"
        >
          <v-list-item-icon class="my-auto mr-2">
            <v-icon class="drag-handle">
              {{ icons.mdiDragVertical }}
            </v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>{{ category.vendibleTagName }}</v-list-item-title>
          </v-list-item-content>

          <v-list-item-action class="my-auto mr-2 flex-row">
            <v-select
              :value="category.icon"
              :items="mappedIconOptions"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              class="icon-select"
              @change="handleIconChange(category.vendibleTagId, $event)"
            >
              <template #selection="{ item }">
                <v-icon small>
                  {{ item.svgIcon }}
                </v-icon>
              </template>
              <template #item="{ item }">
                <v-icon
                  small
                  class="mr-2"
                >
                  {{ item.svgIcon }}
                </v-icon>
                {{ item.text }}
              </template>
            </v-select>
          </v-list-item-action>

          <v-list-item-action class="my-auto">
            <v-btn
              icon
              small
              color="error"
              @click="handleRemoveCategory(category.vendibleTagId)"
            >
              <v-icon small>
                {{ icons.mdiDelete }}
              </v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </div>

    <p
      v-else
      class="text-center grey--text py-4"
    >
      カテゴリが設定されていません。<br>
      上のドロップダウンからカテゴリを追加してください。
    </p>
  </div>
</template>

<script>
import {
  ref,
  watch,
  computed,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api'
import {
  mdiDragVertical,
  mdiDelete,
  mdiPlus,
  mdiTag,
  mdiHome,
  mdiMenu,
  mdiGlassMugVariant,
  mdiGlassWine,
  mdiGlassCocktail,
  mdiCup,
  mdiBottleWine,
  mdiBottleWineOutline,
  mdiSilverwareForkKnife,
  mdiFood,
  mdiCakeVariant,
  mdiShimmer,
  mdiStar,
  mdiHeart,
  mdiGift,
  mdiFlower,
  mdiCamera,
  mdiMusic,
  mdiCog,
} from '@mdi/js'
import Sortable from 'sortablejs'

// アイコン値からSVGパスへのマッピング
const ICON_MAP = {
  default: mdiTag,
  home: mdiHome,
  menu: mdiMenu,
  beer: mdiGlassMugVariant,
  wine: mdiGlassWine,
  cocktail: mdiGlassCocktail,
  drink: mdiCup,
  bottle: mdiBottleWine,
  champagne: mdiBottleWineOutline,
  utensils: mdiSilverwareForkKnife,
  food: mdiFood,
  cake: mdiCakeVariant,
  sparkles: mdiShimmer,
  star: mdiStar,
  heart: mdiHeart,
  gift: mdiGift,
  flower: mdiFlower,
  camera: mdiCamera,
  music: mdiMusic,
  settings: mdiCog,
}

export default {
  name: 'CategorySettings',

  props: {
    categories: {
      type: Array,
      default: () => [],
    },
    availableTags: {
      type: Array,
      default: () => [],
    },
    iconOptions: {
      type: Array,
      default: () => [],
    },
  },

  setup(props, { emit }) {
    const selectedTag = ref(null)
    const localCategories = ref([])
    const sortableContainer = ref(null)
    let sortableInstance = null

    // iconOptionsにSVGパスを追加
    const mappedIconOptions = computed(() => props.iconOptions.map(option => ({
      ...option,
      svgIcon: ICON_MAP[option.value] || mdiTag,
    })))

    watch(
      () => props.categories,
      newVal => {
        localCategories.value = [...newVal]
      },
      { immediate: true, deep: true },
    )

    const handleAddCategory = vendibleTagId => {
      if (vendibleTagId) {
        emit('add', vendibleTagId)
        selectedTag.value = null
      }
    }

    const handleRemoveCategory = vendibleTagId => {
      emit('remove', vendibleTagId)
    }

    const handleIconChange = (vendibleTagId, icon) => {
      emit('update-icon', { vendibleTagId, icon })
    }

    onMounted(() => {
      if (sortableContainer.value) {
        const listEl = sortableContainer.value.querySelector('.v-list')
        if (listEl) {
          sortableInstance = Sortable.create(listEl, {
            handle: '.drag-handle',
            animation: 150,
            onEnd: evt => {
              const { oldIndex, newIndex } = evt
              if (oldIndex !== newIndex) {
                const moved = localCategories.value.splice(oldIndex, 1)[0]
                localCategories.value.splice(newIndex, 0, moved)
                emit('reorder', localCategories.value)
              }
            },
          })
        }
      }
    })

    onBeforeUnmount(() => {
      if (sortableInstance) {
        sortableInstance.destroy()
        sortableInstance = null
      }
    })

    return {
      selectedTag,
      localCategories,
      sortableContainer,
      mappedIconOptions,
      handleAddCategory,
      handleRemoveCategory,
      handleIconChange,

      icons: {
        mdiDragVertical,
        mdiDelete,
        mdiPlus,
      },
    }
  },
}
</script>

<style scoped>
.category-settings {
  min-height: 200px;
}

.category-list {
  padding: 0;
}

.category-item {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-bottom: 8px;
  padding: 8px 12px;
}

.category-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.drag-handle {
  cursor: grab;
  color: rgba(0, 0, 0, 0.38);
}

.drag-handle:hover {
  color: rgba(0, 0, 0, 0.54);
}

.drag-handle:active {
  cursor: grabbing;
}

.icon-select {
  width: 80px;
  max-width: 80px;
}
</style>
